import React, { useEffect, useState, useCallback } from 'react';
import API from '../api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { usePharmacy } from '../context/PharmacyContext';
import { formatAdToBs } from '../utils/nepaliDate';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid
} from 'recharts';
import '../styles/global.css';

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('financial');
  
  // Date range filters for Financials
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Days filter for Near Expiry
  const [expiryDays, setExpiryDays] = useState('90');

  // 1. Initialize Reports Data from Cache
  const getCache = (key, defaultVal) => {
    const cached = sessionStorage.getItem(`reports_${key}`);
    return cached ? JSON.parse(cached) : defaultVal;
  };

  const [plSummary, setPlSummary] = useState(() => getCache('plSummary', null));
  const [stockSummary, setStockSummary] = useState(() => getCache('stockSummary', null));
  const [stockItems, setStockItems] = useState(() => getCache('stockItems', []));
  const [expiryItems, setExpiryItems] = useState(() => getCache('expiryItems', []));
  const [taxData, setTaxData] = useState(() => getCache('taxData', null));
  
  // Fiscal Year state
  const [fiscalYears, setFiscalYears] = useState(() => getCache('fiscalYears', []));
  const [selectedFY, setSelectedFY] = useState('');
  const [showClosingModal, setShowClosingModal] = useState(false);
  const [closingFYName, setClosingFYName] = useState('');
  const [closingNewStartDate, setClosingNewStartDate] = useState('');
  const [closingNewEndDate, setClosingNewEndDate] = useState('');
  const [executingClosing, setExecutingClosing] = useState(false);

  const [loading, setLoading] = useState(false);
  
  // Enhanced responsive breakpoints
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);

  // pharmacyDetails from shared context — no extra API call needed
  const { pharmacyDetails } = usePharmacy();

  const fetchFiscalYears = async () => {
    try {
      const { data } = await API.get('/fiscal-years');
      setFiscalYears(data || []);
      sessionStorage.setItem('reports_fiscalYears', JSON.stringify(data || [])); // Cache FY

      const activeFY = data.find(f => f.status === 'active') || data[0];
      if (activeFY && !selectedFY) {
        setSelectedFY(activeFY._id);
      }
    } catch (e) {
      console.error('Error fetching fiscal years', e);
    }
  };

  const { user } = useAuth();

  // Super Admin Broadcast Fiscal Year State
  const [showSuperAdminFYModal, setShowSuperAdminFYModal] = useState(false);
  const [broadcastFYName, setBroadcastFYName] = useState('FY 2081/82 (BS)');
  const [broadcastStartDate, setBroadcastStartDate] = useState('2024-07-16');
  const [broadcastEndDate, setBroadcastEndDate] = useState('2025-07-15');
  const [broadcastLoading, setBroadcastLoading] = useState(false);

  const handleBroadcastFiscalYear = async (e) => {
    e.preventDefault();
    setBroadcastLoading(true);
    try {
      const { data } = await API.post('/fiscal-years/superadmin/broadcast', {
        name: broadcastFYName,
        startDate: broadcastStartDate,
        endDate: broadcastEndDate
      });
      toast.success(data.message || 'Fiscal Year broadcast successfully!');
      setShowSuperAdminFYModal(false);
      fetchFiscalYears();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to broadcast fiscal year');
    } finally {
      setBroadcastLoading(false);
    }
  };

  const handleActivateFY = async (fyId) => {
    try {
      const { data } = await API.put(`/fiscal-years/activate/${fyId}`);
      toast.success(data.message || 'Fiscal Year activated!');
      fetchFiscalYears();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to activate fiscal year');
    }
  };

  useEffect(() => {
    fetchFiscalYears();
  }, []);

  const handleSelectFY = (fyId) => {
    setSelectedFY(fyId);
    const fy = fiscalYears.find(f => f._id === fyId);
    if (fy) {
      setStartDate(new Date(fy.startDate).toISOString().split('T')[0]);
      setEndDate(new Date(fy.endDate).toISOString().split('T')[0]);
      toast.info(`Switched report view to ${fy.name}`);
    }
  };

  const handleOpenClosingModal = () => {
    const activeFY = fiscalYears.find(f => f.status === 'active') || fiscalYears[0];
    if (!activeFY) {
      toast.warning('No active fiscal year found');
      return;
    }
    const currentAdYear = new Date().getFullYear();
    const month = new Date().getMonth();
    const bsYear = month >= 6 ? currentAdYear + 57 : currentAdYear + 56;
    const nextBsYearShort = (bsYear + 1).toString().slice(-2);

    setClosingFYName(`FY ${bsYear}/${nextBsYearShort} (BS)`);
    setClosingNewStartDate(new Date(currentAdYear, 6, 16).toISOString().split('T')[0]);
    setClosingNewEndDate(new Date(currentAdYear + 1, 6, 15).toISOString().split('T')[0]);
    setShowClosingModal(true);
  };

  const handleExecuteYearEndClosing = async (e) => {
    e.preventDefault();
    const activeFY = fiscalYears.find(f => f.status === 'active') || fiscalYears[0];
    if (!activeFY) return;

    try {
      setExecutingClosing(true);
      const res = await API.post('/fiscal-years/close', {
        fiscalYearId: activeFY._id,
        newFiscalYearName: closingFYName,
        newStartDate: closingNewStartDate,
        newEndDate: closingNewEndDate
      });

      toast.success(res.data.message || 'Year-End Closing completed successfully!');
      setShowClosingModal(false);
      fetchFiscalYears();
      fetchFinancials();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Year-End Closing failed');
    } finally {
      setExecutingClosing(false);
    }
  };


  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width <= 1024 && width > 768);
      setIsSmallMobile(width <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const applyPreset = (preset) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    if (preset === 'THIS_MONTH') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      setStartDate(firstDay);
      setEndDate(todayStr);
    } else if (preset === 'LAST_30') {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      setStartDate(d.toISOString().split('T')[0]);
      setEndDate(todayStr);
    } else if (preset === 'THIS_QUARTER') {
      const quarterMonth = Math.floor(today.getMonth() / 3) * 3;
      const firstDay = new Date(today.getFullYear(), quarterMonth, 1).toISOString().split('T')[0];
      setStartDate(firstDay);
      setEndDate(todayStr);
    } else if (preset === 'THIS_YEAR') {
      const firstDay = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
      setStartDate(firstDay);
      setEndDate(todayStr);
    }
  };

  const handlePrintReport = () => {
    const printWindow = window.open('', '_blank');
    let title = 'Financial Profit & Loss Report';
    let bodyHTML = '';

    if (activeTab === 'financial') {
      title = `Financial P&L Report (${startDate} to ${endDate})`;
      bodyHTML = `
        <h3>Financial Summary</h3>
        <table style="width:100%; border-collapse:collapse; margin-bottom:20px;">
          <tr><td style="padding:8px; border:1px solid #e0f2fe;">Gross Sales Revenue</td><td style="padding:8px; border:1px solid #e0f2fe; font-weight:bold; color:#10b981;">Rs ${fmt(plSummary?.grossRevenue)}</td></tr>
          <tr><td style="padding:8px; border:1px solid #e0f2fe;">Cost of Goods Sold (COGS)</td><td style="padding:8px; border:1px solid #e0f2fe; font-weight:bold; color:#ef4444;">Rs ${fmt(plSummary?.costOfGoodsSold)}</td></tr>
          <tr><td style="padding:8px; border:1px solid #e0f2fe;">Net Profit</td><td style="padding:8px; border:1px solid #e0f2fe; font-weight:bold; color:#1880c2;">Rs ${fmt(plSummary?.netProfit)}</td></tr>
          <tr><td style="padding:8px; border:1px solid #e0f2fe;">Total Tax Collected</td><td style="padding:8px; border:1px solid #e0f2fe; font-weight:bold;">Rs ${fmt(plSummary?.totalTax)}</td></tr>
        </table>
        ${taxData ? `
          <h3>VAT / GST Tax Summary</h3>
          <table style="width:100%; border-collapse:collapse;">
            <tr><td style="padding:8px; border:1px solid #e0f2fe;">Output Tax (Sales)</td><td style="padding:8px; border:1px solid #e0f2fe;">Rs ${fmt(taxData.outputTax)}</td></tr>
            <tr><td style="padding:8px; border:1px solid #e0f2fe;">Input Tax (Purchases)</td><td style="padding:8px; border:1px solid #e0f2fe;">Rs ${fmt(taxData.inputTax)}</td></tr>
            <tr><td style="padding:8px; border:1px solid #e0f2fe; font-weight:bold;">Net Tax Payable</td><td style="padding:8px; border:1px solid #e0f2fe; font-weight:bold;">Rs ${fmt(taxData.netTaxPayable)}</td></tr>
          </table>
        ` : ''}
      `;
    } else if (activeTab === 'stock') {
      title = `Stock Valuation Report`;
      const rows = stockItems.map((item, idx) => `
        <tr>
          <td style="padding:6px; border:1px solid #e0f2fe;">${idx + 1}</td>
          <td style="padding:6px; border:1px solid #e0f2fe; font-weight:bold;">${item.medicineName}</td>
          <td style="padding:6px; border:1px solid #e0f2fe;">${item.batchNumber}</td>
          <td style="padding:6px; border:1px solid #e0f2fe;">${item.quantity}</td>
          <td style="padding:6px; border:1px solid #e0f2fe; text-align:right;">Rs ${fmt(item.costValue)}</td>
          <td style="padding:6px; border:1px solid #e0f2fe; text-align:right;">Rs ${fmt(item.retailValue)}</td>
          <td style="padding:6px; border:1px solid #e0f2fe; text-align:right; color:#10b981;">Rs ${fmt(item.potentialProfit)}</td>
        </tr>
      `).join('');

      bodyHTML = `
        <h3>Stock Inventory Summary</h3>
        <p>Total Batches: ${stockSummary?.totalBatches || 0} | Total Items: ${stockSummary?.totalItemsCount || 0} | Total Cost Value: Rs ${fmt(stockSummary?.totalCostValue)}</p>
        <table style="width:100%; border-collapse:collapse; font-size:11px;">
          <thead>
            <tr style="background:#1880c2; color:#fff;">
              <th style="padding:6px;">#</th>
              <th style="padding:6px;">Medicine</th>
              <th style="padding:6px;">Batch</th>
              <th style="padding:6px;">Qty</th>
              <th style="padding:6px; text-align:right;">Cost Value</th>
              <th style="padding:6px; text-align:right;">Retail Value</th>
              <th style="padding:6px; text-align:right;">Est Profit</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      `;
    } else if (activeTab === 'expiry') {
      title = `Near-Expiry Medicines Report (Within ${expiryDays} Days)`;
      const rows = expiryItems.map((item, idx) => `
        <tr>
          <td style="padding:6px; border:1px solid #e0f2fe;">${idx + 1}</td>
          <td style="padding:6px; border:1px solid #e0f2fe; font-weight:bold;">${item.medicineName}</td>
          <td style="padding:6px; border:1px solid #e0f2fe;">${item.batchNumber}</td>
          <td style="padding:6px; border:1px solid #e0f2fe;">${new Date(item.expiryDate).toLocaleDateString('en-IN')}</td>
          <td style="padding:6px; border:1px solid #e0f2fe;">Rack ${item.rackNumber}</td>
          <td style="padding:6px; border:1px solid #e0f2fe;">${item.quantity}</td>
          <td style="padding:6px; border:1px solid #e0f2fe; font-weight:bold; color:${item.isExpired ? '#dc2626' : '#d97706'};">
            ${item.isExpired ? 'EXPIRED' : `${item.daysRemaining} days`}
          </td>
        </tr>
      `).join('');

      bodyHTML = `
        <h3>Near Expiry Stock Batches</h3>
        <table style="width:100%; border-collapse:collapse; font-size:11px;">
          <thead>
            <tr style="background:#1880c2; color:#fff;">
              <th style="padding:6px;">#</th>
              <th style="padding:6px;">Medicine</th>
              <th style="padding:6px;">Batch</th>
              <th style="padding:6px;">Expiry Date</th>
              <th style="padding:6px;">Location</th>
              <th style="padding:6px;">Qty</th>
              <th style="padding:6px;">Status</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      `;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: sans-serif; padding: 20px; color: #1a202c; }
          .header { text-align: center; border-bottom: 2px solid #1880c2; padding-bottom: 10px; margin-bottom: 20px; }
          .header h2 { margin: 0; color: #1880c2; text-transform: uppercase; }
          .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #718096; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>${pharmacyDetails.name}</h2>
          ${pharmacyDetails.address ? `<p style="margin:2px 0;">${pharmacyDetails.address}</p>` : ''}
          ${pharmacyDetails.pan ? `<p style="margin:2px 0; font-weight:bold;">PAN: ${pharmacyDetails.pan}</p>` : ''}
          <h3 style="margin-top:10px; color:#4a5568;">${title}</h3>
          <p style="font-size:11px; color:#718096; margin:4px 0;">
            Period: ${formatAdToBs(startDate, 'short')} to ${formatAdToBs(endDate, 'short')} (${startDate} to ${endDate} AD) | 
            Generated: ${formatAdToBs(new Date(), 'full')} (${new Date().toLocaleDateString('en-IN')} AD)
          </p>
        </div>
        ${bodyHTML}
        <div class="footer">
          <p>Pharmacy Audit Report | Powered by Pharmacy Ledger Nepal</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  // Fetch Financial P&L and Tax reports
  const fetchFinancials = useCallback(async () => {
    try {
      if (!sessionStorage.getItem('reports_plSummary')) setLoading(true);
      const [plRes, taxRes] = await Promise.all([
        API.get('/reports/profit-loss', { params: { startDate, endDate } }),
        API.get('/reports/tax-report', { params: { startDate, endDate } })
      ]);
      setPlSummary(plRes.data.summary || null);
      setTaxData(taxRes.data || null);
      
      // Cache data
      sessionStorage.setItem('reports_plSummary', JSON.stringify(plRes.data.summary || null));
      sessionStorage.setItem('reports_taxData', JSON.stringify(taxRes.data || null));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load financial reports');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Fetch Stock Valuation reports
  const fetchStockValuation = useCallback(async () => {
    try {
      if (!sessionStorage.getItem('reports_stockSummary')) setLoading(true);
      const res = await API.get('/reports/stock-valuation');
      setStockSummary(res.data.summary || null);
      setStockItems(res.data.items || []);
      
      // Cache data
      sessionStorage.setItem('reports_stockSummary', JSON.stringify(res.data.summary || null));
      sessionStorage.setItem('reports_stockItems', JSON.stringify(res.data.items || []));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load stock valuation reports');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Expiry reports
  const fetchExpiryReport = useCallback(async () => {
    try {
      if (!sessionStorage.getItem('reports_expiryItems')) setLoading(true);
      const res = await API.get('/reports/expiry-report', { params: { days: expiryDays } });
      setExpiryItems(res.data.items || []);
      
      // Cache data
      sessionStorage.setItem('reports_expiryItems', JSON.stringify(res.data.items || []));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load near expiry reports');
    } finally {
      setLoading(false);
    }
  }, [expiryDays]);

  useEffect(() => {
    if (activeTab === 'financial') {
      fetchFinancials();
    } else if (activeTab === 'stock') {
      fetchStockValuation();
    } else if (activeTab === 'expiry') {
      fetchExpiryReport();
    }
  }, [activeTab, fetchFinancials, fetchStockValuation, fetchExpiryReport]);

  const fmt = (num) =>
    Number.isFinite(Number(num))
      ? Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '0.00';

  // Preparing charts data
  const chartsData = plSummary ? [
    {
      name: 'Revenue & COGS',
      Revenue: plSummary.grossRevenue || 0,
      COGS: plSummary.costOfGoodsSold || 0,
      Profit: plSummary.netProfit || 0
    }
  ] : [];

  const styles = {
    container: { 
      minHeight: '100vh', 
      background: '#f4f9fd', // Layout primary blue bg
      fontFamily: "'Inter', sans-serif"
    },
    header: { 
      background: '#ffffff', 
      borderBottom: '1px solid #e0f2fe', // Layout border
      padding: isMobile ? '12px' : isTablet ? '16px 24px' : '20px 32px', 
      display: 'flex', 
      flexDirection: isMobile ? 'column' : 'row', 
      justifyContent: 'space-between', 
      alignItems: isMobile ? 'flex-start' : 'center', 
      gap: isMobile ? 12 : isTablet ? 16 : 0, 
      position: 'sticky', 
      top: 0, 
      zIndex: 100 
    },
    headerTitle: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: isMobile ? 8 : 12 
    },
    headerIcon: { 
      width: isMobile ? 32 : isTablet ? 36 : 40, 
      height: isMobile ? 32 : isTablet ? 36 : 40, 
      background: 'linear-gradient(135deg, #1880c2 0%, #0369a1 100%)', // Blue gradient
      borderRadius: 8, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      color: 'white', 
      fontSize: isMobile ? 16 : 18 
    },
    headerText: { 
      fontSize: isMobile ? 16 : isTablet ? 18 : 20, 
      fontWeight: 700, 
      color: '#1e293b' 
    },
    headerSubtext: { 
      fontSize: isMobile ? 11 : 13, 
      color: '#64748b',
      display: isSmallMobile ? 'none' : 'block'
    },
    headerActions: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: isMobile ? 6 : isTablet ? 8 : 10, 
      width: isMobile ? '100%' : 'auto',
      flexWrap: 'wrap'
    },
    tabBtn: (active) => ({
      padding: isMobile ? '8px 12px' : '10px 20px', 
      background: active ? 'linear-gradient(135deg, #1880c2 0%, #0369a1 100%)' : '#ffffff', // Active Blue, Inactive White
      color: active ? 'white' : '#64748b',
      border: active ? 'none' : '1px solid #e0f2fe', // Border for inactive
      borderRadius: 8, 
      fontSize: isMobile ? 12 : 14, 
      fontWeight: 600, 
      cursor: 'pointer',
      display: 'flex', 
      alignItems: 'center', 
      gap: isMobile ? 4 : 6,
      whiteSpace: 'nowrap',
      flex: isMobile ? '1' : 'none',
      justifyContent: 'center'
    }),
    main: { 
      maxWidth: 1400, 
      margin: '0 auto', 
      padding: isMobile ? '12px' : isTablet ? '16px 24px' : '24px 32px' 
    },
    gridStats: { 
      display: 'grid', 
      gridTemplateColumns: 
        isSmallMobile ? '1fr' : 
        isMobile ? 'repeat(2, 1fr)' : 
        isTablet ? 'repeat(2, 1fr)' : 
        'repeat(4, 1fr)', 
      gap: isMobile ? 12 : 16, 
      marginBottom: isMobile ? 16 : 24 
    },
    statCard: (borderColor = '#e0f2fe') => ({ // Default border layout blue
      background: '#ffffff', 
      borderRadius: 12, 
      padding: isMobile ? 16 : 20, 
      border: `1px solid ${borderColor}`, 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 6 
    }),
    statTitle: { 
      fontSize: isMobile ? 10 : 11, 
      fontWeight: 600, 
      color: '#64748b', 
      textTransform: 'uppercase' 
    },
    statValue: (color = '#1e293b') => ({ 
      fontSize: isMobile ? 18 : isTablet ? 20 : 24, 
      fontWeight: 700, 
      color 
    }),
    card: { 
      background: '#ffffff', 
      borderRadius: 12, 
      border: '1px solid #e0f2fe', 
      marginBottom: isMobile ? 16 : 24, 
      overflow: 'hidden' 
    },
    cardHeader: { 
      padding: isMobile ? '12px 16px' : '16px 24px', 
      borderBottom: '1px solid #e0f2fe', 
      display: 'flex', 
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between', 
      alignItems: isMobile ? 'flex-start' : 'center', 
      background: '#fff',
      gap: isMobile ? 8 : 0
    },
    cardTitle: { 
      fontSize: isMobile ? 14 : 16, 
      fontWeight: 700, 
      color: '#1e293b', 
      display: 'flex', 
      alignItems: 'center', 
      gap: isMobile ? 6 : 10 
    },
    cardBody: { 
      padding: isMobile ? 16 : 24 
    },
    table: { 
      width: '100%', 
      borderCollapse: 'collapse'
    },
    th: { 
      padding: isMobile ? '8px 12px' : '12px 16px', 
      textAlign: 'left', 
      fontSize: isMobile ? 10 : 11, 
      fontWeight: 600, 
      color: '#64748b', 
      background: '#f4f9fd', // Soft blue header
      borderBottom: '1px solid #e0f2fe', 
      textTransform: 'uppercase'
    },
    td: { 
      padding: isMobile ? '8px 12px' : '12px 16px', 
      fontSize: isMobile ? 12 : 14, 
      color: '#1e293b', 
      borderBottom: '1px solid #f4f9fd'
    },
    filterBar: { 
      display: 'flex', 
      gap: isMobile ? 8 : 16, 
      alignItems: isMobile ? 'stretch' : 'center', 
      flexWrap: 'wrap', 
      marginBottom: isMobile ? 16 : 20, 
      background: '#fff', 
      padding: isMobile ? '12px 16px' : '16px 24px', 
      borderRadius: 10, 
      border: '1px solid #e0f2fe', // Matching theme border
      flexDirection: isMobile ? 'column' : 'row'
    },
    input: { 
      padding: isMobile ? '6px 10px' : '8px 12px', 
      borderRadius: 6, 
      border: '1px solid #e1e8ed', 
      fontSize: isMobile ? 12 : 14, 
      outline: 'none',
      width: isMobile ? '100%' : 'auto'
    },
    select: { 
      padding: isMobile ? '6px 10px' : '8px 12px', 
      borderRadius: 6, 
      border: '1px solid #e1e8ed', 
      fontSize: isMobile ? 12 : 14, 
      outline: 'none', 
      background: 'white',
      width: isMobile ? '100%' : 'auto'
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerTitle}>
          <div style={styles.headerIcon}>📊</div>
          <div>
            <div style={styles.headerText}>Reports & Analytics</div>
            <div style={styles.headerSubtext}>Monitor financial health and inventory metrics</div>
          </div>
        </div>
        
        <div style={styles.headerActions}>
          {/* First row of buttons for mobile */}
          {isMobile && (
            <div style={{ display: 'flex', gap: 6, width: '100%' }}>
              <button onClick={() => setActiveTab('financial')} style={styles.tabBtn(activeTab==='financial')}>
                <span>💰</span> {isSmallMobile ? 'P&L' : 'Financials'}
              </button>
              <button onClick={() => setActiveTab('stock')} style={styles.tabBtn(activeTab==='stock')}>
                <span>📦</span> Stock
              </button>
              <button onClick={() => setActiveTab('expiry')} style={styles.tabBtn(activeTab==='expiry')}>
                <span>⏰</span> Expiry
              </button>
            </div>
          )}
          
          {/* Second row for mobile / single row for desktop */}
          <div style={{ 
            display: 'flex', 
            gap: isMobile ? 6 : 10, 
            width: isMobile ? '100%' : 'auto',
            flexWrap: 'wrap'
          }}>
            {!isMobile && user?.role === 'superadmin' && (
              <button
                onClick={() => setShowSuperAdminFYModal(true)}
                style={{ 
                  ...styles.tabBtn(false), 
                  background: 'linear-gradient(135deg, #1880c2 0%, #0369a1 100%)', 
                  color: '#fff', 
                  fontWeight: 700, 
                  boxShadow: '0 4px 12px rgba(24, 128, 194, 0.25)' 
                }}
                title="Super Admin Global Fiscal Year Broadcast"
              >
                <span>👑</span> {isTablet ? 'Global FY' : 'Global FY Governance'}
              </button>
            )}
            
            <select
              value={selectedFY}
              onChange={e => handleSelectFY(e.target.value)}
              style={{ 
                ...styles.select, 
                fontWeight: 600, 
                borderColor: '#1880c2', 
                color: '#0369a1', 
                background: '#e0f2fe', // Matching blue theme
                maxWidth: isMobile ? 'none' : '200px'
              }}
              title="Switch Fiscal Year View"
            >
              {fiscalYears.map(fy => (
  <option key={fy._id} value={fy._id}>
    📅 {fy.name} {fy.status === 'active' ? '(Active)' : '(Closed)'}
  </option>
))}
            </select>
            
            {!isMobile && user?.role === 'superadmin' && selectedFY && fiscalYears.find(f => f._id === selectedFY)?.status !== 'active' && (
              <button
                onClick={() => handleActivateFY(selectedFY)}
                style={{ 
                  ...styles.tabBtn(false), 
                  background: '#16a34a', 
                  color: '#fff', 
                  fontWeight: 600 
                }}
                title="Make Selected Fiscal Year Active"
              >
                <span>⚡</span> Activate FY
              </button>
            )}
            
            <button 
              onClick={handleOpenClosingModal} 
              style={{ 
                ...styles.tabBtn(false), 
                background: '#0369a1', // Dark blue replacement for purple
                color: '#fff' 
              }} 
              title="Execute Year-End Closing"
            >
              <span>🔒</span> {isMobile ? 'Close' : 'Closing'}
            </button>
            
            {!isMobile && (
              <>
                <button onClick={() => setActiveTab('financial')} style={styles.tabBtn(activeTab==='financial')}>
                  <span>💰</span> Financials
                </button>
                <button onClick={() => setActiveTab('stock')} style={styles.tabBtn(activeTab==='stock')}>
                  <span>📦</span> Stock
                </button>
                <button onClick={() => setActiveTab('expiry')} style={styles.tabBtn(activeTab==='expiry')}>
                  <span>⏰</span> Expiry
                </button>
              </>
            )}
            
            <button 
              onClick={handlePrintReport} 
              style={{ 
                ...styles.tabBtn(false), 
                background: '#1880c2', 
                color: '#fff' 
              }}
            >
              <span>🖨️</span> {isMobile ? 'Print' : 'Export'}
            </button>
          </div>
          
          {/* Mobile-only Super Admin button */}
          {isMobile && user?.role === 'superadmin' && (
            <button
              onClick={() => setShowSuperAdminFYModal(true)}
              style={{ 
                ...styles.tabBtn(false), 
                background: 'linear-gradient(135deg, #1880c2 0%, #0369a1 100%)', 
                color: '#fff', 
                fontWeight: 700, 
                width: '100%'
              }}
            >
              <span>👑</span> Global FY Governance
            </button>
          )}
        </div>
      </header>

      <main style={styles.main}>
        {/* FINANCIALS TAB */}
        {activeTab === 'financial' && (
          <div>
            {/* Filter Bar */}
            <div style={styles.filterBar}>
              <span style={{ 
                fontWeight: 600, 
                color: '#475569',
                fontSize: isMobile ? 12 : 14,
                width: isMobile ? '100%' : 'auto'
              }}>
                Date Range:
              </span>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6, 
                flexWrap: 'wrap',
                width: isMobile ? '100%' : 'auto'
              }}>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  style={styles.input}
                />
                {!isSmallMobile && (
                  <span style={{ 
                    fontSize: 10, 
                    padding: '4px 8px', 
                    background: '#f4f9fd', // Soft blue
                    color: '#1880c2', 
                    border: '1px solid #e0f2fe', 
                    borderRadius: 6, 
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}>
                    🇳🇵 {formatAdToBs(startDate, 'short')}
                  </span>
                )}
              </div>
              
              <span style={{ color: '#94a3b8', alignSelf: 'center' }}>to</span>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6, 
                flexWrap: 'wrap',
                width: isMobile ? '100%' : 'auto'
              }}>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  style={styles.input}
                />
                {!isSmallMobile && (
                  <span style={{ 
                    fontSize: 10, 
                    padding: '4px 8px', 
                    background: '#f4f9fd', // Soft blue
                    color: '#1880c2', 
                    border: '1px solid #e0f2fe', 
                    borderRadius: 6, 
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}>
                    🇳🇵 {formatAdToBs(endDate, 'short')}
                  </span>
                )}
              </div>
              
              <button
                onClick={fetchFinancials}
                style={{ 
                  padding: isMobile ? '8px 16px' : '8px 16px', 
                  background: '#1880c2', // Primary Blue
                  color: 'white', 
                  border: 'none', 
                  borderRadius: 6, 
                  fontWeight: 600, 
                  cursor: 'pointer',
                  fontSize: isMobile ? 12 : 14,
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Apply Filter
              </button>

              <div style={{ 
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: 6,
                width: isMobile ? '100%' : 'auto'
              }}>
                <button 
                  type="button" 
                  onClick={() => applyPreset('THIS_MONTH')} 
                  style={{ 
                    padding: '6px 12px', 
                    background: '#ffffff', 
                    border: '1px solid #e0f2fe', 
                    borderRadius: 6, 
                    fontSize: 11, 
                    fontWeight: 600, 
                    cursor: 'pointer' 
                  }}
                >
                  {isMobile ? 'Month' : 'This Month'}
                </button>
                <button 
                  type="button" 
                  onClick={() => applyPreset('LAST_30')} 
                  style={{ 
                    padding: '6px 12px', 
                    background: '#ffffff', 
                    border: '1px solid #e0f2fe', 
                    borderRadius: 6, 
                    fontSize: 11, 
                    fontWeight: 600, 
                    cursor: 'pointer' 
                  }}
                >
                  {isMobile ? '30D' : 'Last 30 Days'}
                </button>
                <button 
                  type="button" 
                  onClick={() => applyPreset('THIS_QUARTER')} 
                  style={{ 
                    padding: '6px 12px', 
                    background: '#ffffff', 
                    border: '1px solid #e0f2fe', 
                    borderRadius: 6, 
                    fontSize: 11, 
                    fontWeight: 600, 
                    cursor: 'pointer' 
                  }}
                >
                  {isMobile ? 'Q' : 'Quarter'}
                </button>
                <button 
                  type="button" 
                  onClick={() => applyPreset('THIS_YEAR')} 
                  style={{ 
                    padding: '6px 12px', 
                    background: '#ffffff', 
                    border: '1px solid #e0f2fe', 
                    borderRadius: 6, 
                    fontSize: 11, 
                    fontWeight: 600, 
                    cursor: 'pointer' 
                  }}
                >
                  {isMobile ? 'Year' : 'This Year'}
                </button>
              </div>
            </div>

            {/* Quick Cards */}
            {plSummary && (
              <div style={styles.gridStats}>
                <div style={styles.statCard('#e0f2fe')}>
                  <span style={styles.statTitle}>Gross Sales Revenue</span>
                  <span style={styles.statValue('#10b981')}>Rs {fmt(plSummary.grossRevenue)}</span>
                </div>
                <div style={styles.statCard('#fecaca')}>
                  <span style={styles.statTitle}>Cost of Goods Sold (COGS)</span>
                  <span style={styles.statValue('#ef4444')}>Rs {fmt(plSummary.costOfGoodsSold)}</span>
                </div>
                <div style={styles.statCard('#bbf7d0')}>
                  <span style={styles.statTitle}>Net profit</span>
                  <span style={styles.statValue(plSummary.netProfit >= 0 ? '#10b981' : '#dc2626')}>
                    Rs {fmt(plSummary.netProfit)}
                  </span>
                </div>
                <div style={styles.statCard('#e0f2fe')}>
                  <span style={styles.statTitle}>Total Taxes (Collected)</span>
                  <span style={styles.statValue('#1880c2')}>Rs {fmt(plSummary.totalTax)}</span>
                </div>
              </div>
            )}

            {/* Visual Charts & Tax summaries */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : '2fr 1.2fr', 
              gap: isMobile ? 16 : 24 
            }}>
              {/* Profit graph */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardTitle}><span>📈</span> Revenue vs Profit Analysis</div>
                </div>
                <div style={{ 
                  ...styles.cardBody, 
                  height: isMobile ? 250 : isTablet ? 300 : 350 
                }}>
                  {loading && chartsData.length === 0 ? (
                    <div style={{ 
                      display: 'flex', 
                      height: '100%', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: '#94a3b8'
                    }}>
                      Loading chart...
                    </div>
                  ) : chartsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartsData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" fontSize={isMobile ? 10 : 12} />
                        <YAxis fontSize={isMobile ? 10 : 12} />
                        <Tooltip formatter={(value) => `Rs ${fmt(value)}`} />
                        <Legend />
                        <Bar dataKey="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="COGS" fill="#ef4444" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Profit" fill="#1880c2" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: isMobile ? '30px 20px' : '50px', 
                      color: '#94a3b8' 
                    }}>
                      No sales data for this period
                    </div>
                  )}
                </div>
              </div>

              {/* Tax summary ledger */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardTitle}><span>🧾</span> VAT/GST Tax Summary</div>
                </div>
                <div style={styles.cardBody}>
                  {taxData ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        paddingBottom: 12, 
                        borderBottom: '1px solid #f4f9fd',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 4 : 0
                      }}>
                        <span style={{ 
                          color: '#475569', 
                          fontWeight: 500,
                          fontSize: isMobile ? 12 : 14
                        }}>
                          Output Tax (Sales collected):
                        </span>
                        <strong style={{ 
                          color: '#10b981',
                          fontSize: isMobile ? 14 : 16
                        }}>
                          Rs {fmt(taxData.outputTax)}
                        </strong>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        paddingBottom: 12, 
                        borderBottom: '1px solid #f4f9fd',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 4 : 0
                      }}>
                        <span style={{ 
                          color: '#475569', 
                          fontWeight: 500,
                          fontSize: isMobile ? 12 : 14
                        }}>
                          Input Tax (Purchases paid):
                        </span>
                        <strong style={{ 
                          color: '#ef4444',
                          fontSize: isMobile ? 14 : 16
                        }}>
                          Rs {fmt(taxData.inputTax)}
                        </strong>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        background: '#f4f9fd', 
                        padding: '12px 16px', 
                        borderRadius: 8,
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 4 : 0
                      }}>
                        <strong style={{ 
                          color: '#1e293b',
                          fontSize: isMobile ? 12 : 14
                        }}>
                          Net Tax Payable:
                        </strong>
                        <strong style={{ 
                          color: taxData.netTaxPayable >= 0 ? '#10b981' : '#dc2626', 
                          fontSize: isMobile ? 16 : 16 
                        }}>
                          Rs {fmt(taxData.netTaxPayable)}
                        </strong>
                      </div>
                      <p style={{ 
                        margin: 0, 
                        fontSize: isMobile ? 10 : 12, 
                        color: '#94a3b8', 
                        lineHeight: 1.4 
                      }}>
                        * Net tax payable is Output Tax minus Input Tax. A negative value represents tax input credits that can be claimed.
                      </p>
                    </div>
                  ) : (
                    <div>No tax details available.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STOCK VALUATION TAB */}
        {activeTab === 'stock' && (
          <div>
            {/* Quick Cards */}
            {stockSummary && (
              <div style={styles.gridStats}>
                <div style={styles.statCard('#e0f2fe')}>
                  <span style={styles.statTitle}>Unique Batches in Stock</span>
                  <span style={styles.statValue()}>{stockSummary.totalBatches}</span>
                </div>
                <div style={styles.statCard('#e0f2fe')}>
                  <span style={styles.statTitle}>Total Stock Items</span>
                  <span style={styles.statValue()}>{stockSummary.totalItemsCount}</span>
                </div>
                <div style={styles.statCard('#bbf7d0')}>
                  <span style={styles.statTitle}>Total Cost Value (At cost)</span>
                  <span style={styles.statValue('#1880c2')}>Rs {fmt(stockSummary.totalCostValue)}</span>
                </div>
                <div style={styles.statCard('#e0f2fe')}>
                  <span style={styles.statTitle}>Potential Profit Margin</span>
                  <span style={styles.statValue('#10b981')}>Rs {fmt(stockSummary.potentialProfit)}</span>
                </div>
              </div>
            )}

            {/* Stock Valuation Table */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardTitle}><span>📋</span> Active Stock Batches Valuation</div>
              </div>
              <div style={styles.cardBody}>
                <div style={{ 
                  overflowX: 'auto',
                  marginBottom: isMobile ? 16 : 0
                }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Medicine Name</th>
                        <th style={styles.th}>Batch #</th>
                        <th style={styles.th}>Expiry</th>
                        <th style={styles.th}>Qty</th>
                        <th style={styles.th}>Cost (Per Unit)</th>
                        <th style={styles.th}>Cost Value</th>
                        <th style={styles.th}>Selling Value</th>
                        <th style={styles.th}>Est. Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockItems.map(item => (
                        <tr key={item.batchId}>
                          <td style={{ ...styles.td, fontWeight: 600 }}>{item.medicineName}</td>
                          <td style={styles.td}>{item.batchNumber}</td>
                          <td style={styles.td}>{new Date(item.expiryDate).toLocaleDateString('en-IN')}</td>
                          <td style={styles.td}>{item.quantity}</td>
                          <td style={styles.td}>Rs {fmt(item.costPrice)}</td>
                          <td style={styles.td}>Rs {fmt(item.costValue)}</td>
                          <td style={{ ...styles.td, fontWeight: 600 }}>Rs {fmt(item.retailValue)}</td>
                          <td style={{ ...styles.td, color: '#10b981', fontWeight: 600 }}>Rs {fmt(item.potentialProfit)}</td>
                        </tr>
                      ))}
                      {loading && stockItems.length === 0 && (
                        <tr>
                          <td colSpan="8" style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                            Loading stock data...
                          </td>
                        </tr>
                      )}
                      {stockItems.length === 0 && !loading && (
                        <tr>
                          <td colSpan="8" style={{ 
                            ...styles.td, 
                            textAlign: 'center', 
                            padding: isMobile ? '24px' : '32px', 
                            color: '#94a3b8' 
                          }}>
                            No active stock records found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* EXPIRY INSPECTOR TAB */}
        {activeTab === 'expiry' && (
          <div>
            {/* Filter Bar */}
            <div style={styles.filterBar}>
              <span style={{ 
                fontWeight: 600, 
                color: '#475569',
                fontSize: isMobile ? 12 : 14,
                width: isMobile ? '100%' : 'auto'
              }}>
                Find batches expiring within:
              </span>
              <select
                value={expiryDays}
                onChange={e => setExpiryDays(e.target.value)}
                style={styles.select}
              >
                <option value="30">30 Days (Urgent)</option>
                <option value="60">60 Days</option>
                <option value="90">90 Days (3 Months)</option>
                <option value="180">180 Days (6 Months)</option>
                <option value="365">365 Days (1 Year)</option>
              </select>
            </div>

            {/* Expiry Table */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardTitle}>
                  <span>⏰</span> Near-Expiry or Expired Medicine Batches
                </div>
              </div>
              <div style={styles.cardBody}>
                <div style={{ 
                  overflowX: 'auto',
                  marginBottom: isMobile ? 16 : 0
                }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Medicine Name</th>
                        <th style={styles.th}>Batch No</th>
                        <th style={styles.th}>Expiry Date</th>
                        <th style={styles.th}>Rack Location</th>
                        <th style={styles.th}>Stock Qty</th>
                        <th style={styles.th}>Days Remaining</th>
                        <th style={styles.th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expiryItems.map(item => (
                        <tr 
                          key={item.batchId} 
                          style={{ background: item.isExpired ? '#fef2f2' : 'transparent' }}
                        >
                          <td style={{ ...styles.td, fontWeight: 600 }}>{item.medicineName}</td>
                          <td style={styles.td}>{item.batchNumber}</td>
                          <td style={styles.td}>{new Date(item.expiryDate).toLocaleDateString('en-IN')}</td>
                          <td style={styles.td}>{item.rackNumber}</td>
                          <td style={styles.td}>{item.quantity}</td>
                          <td style={styles.td}>
                            {item.isExpired ? (
                              <span style={{ color: '#ef4444', fontWeight: 600 }}>Expired</span>
                            ) : (
                              <span>{item.daysRemaining} days</span>
                            )}
                          </td>
                          <td style={styles.td}>
                            {item.isExpired ? (
                              <span style={{ 
                                padding: '4px 8px', 
                                background: '#fecaca', 
                                color: '#dc2626', 
                                borderRadius: 6, 
                                fontSize: isMobile ? 10 : 12, 
                                fontWeight: 600 
                              }}>
                                EXPIRED
                              </span>
                            ) : item.daysRemaining <= 30 ? (
                              <span style={{ 
                                padding: '4px 8px', 
                                background: '#fef3c7', 
                                color: '#d97706', 
                                borderRadius: 6, 
                                fontSize: isMobile ? 10 : 12, 
                                fontWeight: 600 
                              }}>
                                URGENT
                              </span>
                            ) : (
                              <span style={{ 
                                padding: '4px 8px', 
                                background: '#e0f2fe', 
                                color: '#1880c2', 
                                borderRadius: 6, 
                                fontSize: isMobile ? 10 : 12, 
                                fontWeight: 600 
                              }}>
                                NEAR EXPIRY
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {loading && expiryItems.length === 0 && (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                            Loading expiry data...
                          </td>
                        </tr>
                      )}
                      {expiryItems.length === 0 && !loading && (
                        <tr>
                          <td colSpan="7" style={{ 
                            ...styles.td, 
                            textAlign: 'center', 
                            padding: isMobile ? '24px' : '32px', 
                            color: '#94a3b8' 
                          }}>
                            Awesome! No medicines are expiring in this range.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Year-End Closing Wizard Modal */}
      {showClosingModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(15, 23, 42, 0.6)', 
          backdropFilter: 'blur(4px)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 1000, 
          padding: isMobile ? 16 : 20 
        }} onClick={() => setShowClosingModal(false)}>
          <div style={{ 
            background: '#ffffff', 
            borderRadius: 16, 
            maxWidth: isMobile ? '100%' : 560, 
            width: '100%', 
            border: '1px solid #e0f2fe', 
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', 
            overflow: 'hidden',
            maxHeight: isMobile ? '90vh' : 'auto',
            overflowY: 'auto'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ 
              padding: isMobile ? '16px 20px' : '20px 24px', 
              borderBottom: '1px solid #e0f2fe', 
              background: 'linear-gradient(135deg, #1880c2 0%, #0369a1 100%)', 
              color: 'white', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start' 
            }}>
              <div>
                <h2 style={{ 
                  fontSize: isMobile ? 16 : 18, 
                  fontWeight: 700, 
                  margin: 0 
                }}>
                  🔒 Financial Year-End Closing Wizard
                </h2>
                <p style={{ 
                  fontSize: isMobile ? 10 : 12, 
                  opacity: 0.9, 
                  margin: '4px 0 0' 
                }}>
                  Close active fiscal period, carry forward balances, and reset P&L
                </p>
              </div>
              <button 
                onClick={() => setShowClosingModal(false)} 
                style={{ 
                  background: 'rgba(255,255,255,0.2)', 
                  border: 'none', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: 32, 
                  height: 32, 
                  cursor: 'pointer', 
                  fontSize: 18 
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleExecuteYearEndClosing} style={{ padding: isMobile ? 20 : 24 }}>
              <div style={{ 
                background: '#f4f9fd', 
                padding: 16, 
                borderRadius: 10, 
                border: '1px solid #e0f2fe', 
                marginBottom: 20 
              }}>
                <h4 style={{ 
                  margin: '0 0 8px', 
                  fontSize: isMobile ? 12 : 14, 
                  fontWeight: 700, 
                  color: '#1e293b' 
                }}>
                  Summary of Ending Fiscal Year
                </h4>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                  gap: 10, 
                  fontSize: isMobile ? 11 : 13 
                }}>
                  <div>Total Revenue: <strong style={{ color: '#059669' }}>Rs {fmt(plSummary?.grossRevenue || 0)}</strong></div>
                  <div>Total Purchases: <strong style={{ color: '#2563eb' }}>Rs {fmt(plSummary?.costOfGoodsSold || 0)}</strong></div>
                  <div>Net Profit: <strong style={{ color: '#1880c2' }}>Rs {fmt(plSummary?.netProfit || 0)}</strong></div>
                  <div>Stock Valuation: <strong style={{ color: '#d97706' }}>Rs {fmt(stockSummary?.totalCostValue || 0)}</strong></div>
                </div>
              </div>

              <div style={{ 
                background: '#e0f2fe', 
                padding: 14, 
                borderRadius: 10, 
                border: '1px solid #bae6fd', 
                marginBottom: 20, 
                fontSize: isMobile ? 11 : 13, 
                color: '#0369a1' 
              }}>
                <strong>🔄 Automatic Carry-Forward Actions:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                  <li>Customer & Supplier ending balances carry forward as new <strong>Opening Balances</strong>.</li>
                  <li>Current inventory stock value carries forward as <strong>Opening Stock</strong>.</li>
                  <li>Net Profit (Rs {fmt(plSummary?.netProfit || 0)}) will be credited to your <strong>Capital Equity Account</strong>.</li>
                </ul>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ 
                    fontSize: isMobile ? 11 : 13, 
                    fontWeight: 600, 
                    color: '#334155', 
                    display: 'block', 
                    marginBottom: 6 
                  }}>
                    New Fiscal Year Name *
                  </label>
                  <input
                    type="text"
                    value={closingFYName}
                    onChange={e => setClosingFYName(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px', 
                      borderRadius: 8, 
                      border: '1px solid #cbd5e1', 
                      fontSize: isMobile ? 12 : 14 
                    }}
                    placeholder="e.g. FY 2081/82"
                    required
                  />
                </div>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                  gap: 12 
                }}>
                  <div>
                    <label style={{ 
                      fontSize: isMobile ? 11 : 13, 
                      fontWeight: 600, 
                      color: '#334155', 
                      display: 'block', 
                      marginBottom: 6 
                    }}>
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={closingNewStartDate}
                      onChange={e => setClosingNewStartDate(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        borderRadius: 8, 
                        border: '1px solid #cbd5e1', 
                        fontSize: isMobile ? 12 : 14 
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ 
                      fontSize: isMobile ? 11 : 13, 
                      fontWeight: 600, 
                      color: '#334155', 
                      display: 'block', 
                      marginBottom: 6 
                    }}>
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={closingNewEndDate}
                      onChange={e => setClosingNewEndDate(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        borderRadius: 8, 
                        border: '1px solid #cbd5e1', 
                        fontSize: isMobile ? 12 : 14 
                      }}
                      required
                    />
                  </div>
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: 12, 
                marginTop: 24, 
                paddingTop: 16, 
                borderTop: '1px solid #e0f2fe',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <button 
                  type="button" 
                  onClick={() => setShowClosingModal(false)} 
                  style={{ 
                    padding: '10px 20px', 
                    borderRadius: 8, 
                    border: '1px solid #cbd5e1', 
                    background: 'white', 
                    fontWeight: 600, 
                    cursor: 'pointer',
                    fontSize: isMobile ? 12 : 14
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={executingClosing} 
                  style={{ 
                    padding: '10px 20px', 
                    borderRadius: 8, 
                    border: 'none', 
                    background: 'linear-gradient(135deg, #1880c2 0%, #0369a1 100%)', 
                    color: 'white', 
                    fontWeight: 700, 
                    cursor: 'pointer',
                    fontSize: isMobile ? 12 : 14
                  }}
                >
                  {executingClosing ? 'Executing...' : (isMobile ? 'Confirm Closing' : 'Confirm Year-End Closing')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SUPER ADMIN GLOBAL FISCAL YEAR GOVERNANCE MODAL */}
      {showSuperAdminFYModal && (
        <div style={{ 
          position: 'fixed', 
          inset: 0, 
          background: 'rgba(15, 23, 42, 0.6)', 
          backdropFilter: 'blur(4px)', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          zIndex: 99999, 
          padding: isMobile ? 16 : 16 
        }}>
          <div style={{ 
            background: 'white', 
            borderRadius: 16, 
            maxWidth: isMobile ? '100%' : 540, 
            width: '100%', 
            overflow: 'hidden', 
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            maxHeight: isMobile ? '90vh' : 'auto',
            overflowY: 'auto'
          }}>
            <div style={{ 
              background: 'linear-gradient(135deg, #1880c2 0%, #0369a1 100%)', 
              padding: isMobile ? '16px 20px' : '20px 24px', 
              color: 'white', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start' 
            }}>
              <div>
                <span style={{ 
                  fontSize: isMobile ? 8 : 10, 
                  background: 'rgba(255,255,255,0.2)', 
                  padding: '3px 10px', 
                  borderRadius: 12, 
                  fontWeight: 700, 
                  textTransform: 'uppercase' 
                }}>
                  👑 Super Admin Governance
                </span>
                <h3 style={{ 
                  margin: '4px 0 0', 
                  fontSize: isMobile ? 16 : 18, 
                  fontWeight: 800 
                }}>
                  Broadcast Active Fiscal Year
                </h3>
              </div>
              <button 
                onClick={() => setShowSuperAdminFYModal(false)} 
                style={{ 
                  background: 'rgba(255,255,255,0.2)', 
                  border: 'none', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: 32, 
                  height: 32, 
                  cursor: 'pointer', 
                  fontWeight: 700 
                }}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleBroadcastFiscalYear} style={{ padding: isMobile ? 20 : 24 }}>
              <div style={{ 
                background: '#e0f2fe', 
                border: '1px solid #bae6fd', 
                borderRadius: 10, 
                padding: 14, 
                marginBottom: 16, 
                fontSize: isMobile ? 11 : 13, 
                color: '#0369a1' 
              }}>
                <strong>🚀 Multi-Tenant Global Activation:</strong>
                <p style={{ margin: '4px 0 0', fontSize: isMobile ? 10 : 12 }}>
                  Broadcasting will create/activate this Bikram Sambat (BS) Fiscal Year for <strong>ALL registered pharmacy tenant accounts</strong> across Nepal.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ 
                    fontSize: isMobile ? 10 : 12, 
                    fontWeight: 700, 
                    color: '#475569', 
                    display: 'block', 
                    marginBottom: 6, 
                    textTransform: 'uppercase' 
                  }}>
                    Fiscal Year Title (BS) *
                  </label>
                  <input
                    type="text"
                    value={broadcastFYName}
                    onChange={e => setBroadcastFYName(e.target.value)}
                    placeholder="e.g. FY 2081/82 (BS)"
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px', 
                      borderRadius: 8, 
                      border: '1px solid #cbd5e1', 
                      fontSize: isMobile ? 12 : 14, 
                      fontWeight: 600 
                    }}
                    required
                  />
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                  gap: 12 
                }}>
                  <div>
                    <label style={{ 
                      fontSize: isMobile ? 10 : 12, 
                      fontWeight: 700, 
                      color: '#475569', 
                      display: 'block', 
                      marginBottom: 6, 
                      textTransform: 'uppercase' 
                    }}>
                      Start Date (Shrawan 1) *
                    </label>
                    <input
                      type="date"
                      value={broadcastStartDate}
                      onChange={e => setBroadcastStartDate(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        borderRadius: 8, 
                        border: '1px solid #cbd5e1', 
                        fontSize: isMobile ? 12 : 14 
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ 
                      fontSize: isMobile ? 10 : 12, 
                      fontWeight: 700, 
                      color: '#475569', 
                      display: 'block', 
                      marginBottom: 6, 
                      textTransform: 'uppercase' 
                    }}>
                      End Date (Ashadh End) *
                    </label>
                    <input
                      type="date"
                      value={broadcastEndDate}
                      onChange={e => setBroadcastEndDate(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        borderRadius: 8, 
                        border: '1px solid #cbd5e1', 
                        fontSize: isMobile ? 12 : 14 
                      }}
                      required
                    />
                  </div>
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: 12, 
                marginTop: 24, 
                paddingTop: 16, 
                borderTop: '1px solid #e0f2fe',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <button 
                  type="button" 
                  onClick={() => setShowSuperAdminFYModal(false)} 
                  style={{ 
                    padding: '10px 18px', 
                    borderRadius: 8, 
                    border: '1px solid #cbd5e1', 
                    background: 'white', 
                    fontWeight: 600, 
                    cursor: 'pointer',
                    fontSize: isMobile ? 12 : 14
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={broadcastLoading} 
                  style={{ 
                    padding: '10px 22px', 
                    borderRadius: 8, 
                    border: 'none', 
                    background: 'linear-gradient(135deg, #1880c2 0%, #0369a1 100%)', 
                    color: 'white', 
                    fontWeight: 700, 
                    cursor: 'pointer', 
                    boxShadow: '0 4px 12px rgba(24, 128, 194, 0.25)',
                    fontSize: isMobile ? 12 : 14
                  }}
                >
                  {broadcastLoading ? 'Broadcasting...' : (isMobile ? '🚀 Broadcast for All' : '🚀 Broadcast & Activate for All Pharmacies')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}